import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  ArrowLeftRight,
  Briefcase,
  Activity,
  RefreshCw,
  XCircle,
  Server,
  Package,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { KpiMetricCard } from '../components/dashboard/KpiMetricCard';
import { AlertActionBanner } from '../components/dashboard/AlertActionBanner';
import { SupplierFloatTable, type SupplierFloatRow } from '../components/dashboard/SupplierFloatTable';
import { formatZAR, formatCompactNumber } from '../utils/format';

interface DashboardData {
  kpis: {
    activeUsers: { total: number; kycVerified: number };
    transactions: { totalValueZar: number; totalVolume: number };
    apiPayments: { totalValueZar: number; fulfilledCount: number; inFlightCount: number };
    partnersSuppliers: {
      activeChannelPartners: number;
      activeSuppliers: number;
      catalogProducts: number;
      cpRunsLast7d: number;
    };
    platformHealth: {
      status: 'healthy' | 'degraded' | 'unhealthy';
      uptimeSeconds: number;
      uptimePercentSinceStart: number;
      dbLatencyMs: number;
      lastCheckedAt: string;
      cpuPercent?: number | null;
    };
  };
  supplierFloats: SupplierFloatRow[];
  alerts: Array<{ type: string; title: string; message: string }>;
  meta: { cachedAt: string; refreshRecommendedSec: number };
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="h-8 w-64 animate-pulse rounded-lg bg-[var(--muted)]" />
          <div className="h-4 w-48 animate-pulse rounded-md bg-[var(--muted)]" />
        </div>
        <div className="h-9 w-24 shrink-0 animate-pulse rounded-lg bg-[var(--muted)]" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="animate-pulse rounded-xl border border-[var(--border)] border-l-4 border-l-[var(--muted)] bg-[var(--card)] p-5 shadow-sm"
          >
            <div className="mb-4 h-3 w-24 rounded bg-[var(--muted)]" />
            <div className="h-8 w-32 rounded-md bg-[var(--muted)]" />
          </div>
        ))}
      </div>
      <div className="animate-pulse overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
        <div className="mb-4 h-5 w-40 rounded bg-[var(--muted)]" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((j) => (
            <div key={j} className="h-10 rounded bg-[var(--muted)]" />
          ))}
        </div>
      </div>
    </div>
  );
}

const HEALTH_STATUS_LABEL: Record<string, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  unhealthy: 'Unhealthy',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError('');

      const token = sessionStorage.getItem('portal_token');
      if (!token) {
        navigate('/admin/login', { replace: true });
        return;
      }

      try {
        const res = await fetch('/api/v1/admin/dashboard', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401) {
          sessionStorage.removeItem('portal_token');
          sessionStorage.removeItem('portal_user');
          navigate('/admin/login', { replace: true });
          return;
        }

        const json = await res.json();
        if (json.success) {
          setData(json.data);
        } else {
          setError(json.error || 'Failed to load dashboard');
        }
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) return <DashboardSkeleton />;

  if (error) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center p-6">
        <div className="w-full max-w-md rounded-xl border-2 border-red-500 bg-[var(--card)] p-6 shadow-sm">
          <div className="flex flex-col items-center gap-4 text-center">
            <XCircle className="h-12 w-12 text-red-600" aria-hidden />
            <p className="text-sm font-medium text-[var(--foreground)]">{error}</p>
            <button
              type="button"
              onClick={() => fetchDashboard()}
              className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, supplierFloats, alerts } = data;
  const healthStatus =
    HEALTH_STATUS_LABEL[kpis.platformHealth.status] || HEALTH_STATUS_LABEL.healthy;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const displayName =
    user?.entityName?.trim() ||
    (user?.email ? user.email.split('@')[0] : '') ||
    'there';
  const todayFormatted = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[var(--foreground)]">
            {greeting}, {displayName}
          </h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">{todayFormatted}</p>
          <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
            Treasury control room
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchDashboard(true)}
          disabled={refreshing}
          aria-label="Refresh dashboard data"
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm font-medium text-[var(--foreground)] shadow-sm transition hover:bg-[var(--muted)]/50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
          Refresh
        </button>
      </div>

      <AlertActionBanner alerts={alerts} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <KpiMetricCard
          label="Wallet"
          metrics={[
            { value: kpis.activeUsers.total.toLocaleString('en-ZA'), label: 'active users' },
            { value: formatZAR(kpis.transactions.totalValueZar), label: 'completed' },
          ]}
          footnote={`${kpis.activeUsers.kycVerified.toLocaleString('en-ZA')} KYC verified · ${formatCompactNumber(kpis.transactions.totalVolume)} transactions`}
          icon={Users}
          accent="green"
          onClick={() => navigate('/admin/transactions')}
        />
        <KpiMetricCard
          label="API payments"
          metrics={[{ value: formatZAR(kpis.apiPayments.totalValueZar) }]}
          footnote={`${formatCompactNumber(kpis.apiPayments.fulfilledCount)} fulfilled · ${formatCompactNumber(kpis.apiPayments.inFlightCount)} in progress`}
          icon={ArrowLeftRight}
          accent="blue"
          onClick={() => navigate('/admin/channel-partners')}
        />
        <KpiMetricCard
          label="Channel Partners"
          metrics={[{ value: kpis.partnersSuppliers.activeChannelPartners.toLocaleString('en-ZA') }]}
          footnote={`${kpis.partnersSuppliers.cpRunsLast7d} disbursement runs (7d)`}
          icon={Briefcase}
          accent="purple"
          onClick={() => navigate('/admin/channel-partners')}
        />
        <KpiMetricCard
          label="Suppliers"
          metrics={[{ value: kpis.partnersSuppliers.activeSuppliers.toLocaleString('en-ZA') }]}
          footnote={`${kpis.partnersSuppliers.catalogProducts.toLocaleString('en-ZA')} catalog products`}
          icon={Package}
          accent="orange"
          onClick={() => {
            const el = document.getElementById('supplier-floats');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
        <KpiMetricCard
          label="Platform Health"
          metrics={[
            {
              value: `${kpis.platformHealth.uptimePercentSinceStart}%`,
              label: '24h uptime',
            },
          ]}
          footnote={`${healthStatus} · DB ${kpis.platformHealth.dbLatencyMs}ms${
            kpis.platformHealth.cpuPercent != null ? ` · CPU ${kpis.platformHealth.cpuPercent}%` : ''
          }`}
          icon={Server}
          accent="blue"
          onClick={() => navigate('/admin/system')}
        />
      </div>

      <div
        id="supplier-floats"
        className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-[var(--portal-shadow)]"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] bg-gradient-to-r from-[#2D8CCA]/[0.04] to-transparent px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2D8CCA]/10">
              <Activity className="h-5 w-5 text-[var(--mymoolah-blue)]" aria-hidden />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--foreground)]">Supplier floats</h2>
              <p className="text-xs text-[var(--muted-foreground)]">Live balances vs minimum thresholds</p>
            </div>
            <span className="ml-1 inline-flex rounded-full border border-[var(--border)] bg-[var(--card)] px-2.5 py-0.5 text-xs font-semibold text-[var(--muted-foreground)]">
              {supplierFloats.length} active
            </span>
          </div>
        </div>
        <SupplierFloatTable
          rows={supplierFloats}
          onRowClick={(supplierId) => navigate(`/admin/floats/${encodeURIComponent(supplierId)}`)}
        />
      </div>
    </div>
  );
}
